import app from 'flarum/forum/app';
import Api from './Api';
import LessonReviewModal from '../modal/LessonReviewModal';

// ⚠️ v0.18.0 — 媒体三件套 (灯箱 / COS 上传 / 类型判定) 住在 LessonReviewMedia.js,
//   **本文件只 re-export**。原因是它们原本住在这里, 而 LessonReviewModal 又要 import 它们,
//   于是形成 `LessonReviewModal ⇄ LessonReviews` 的**循环依赖**; 实测该环会让 modal 类在
//   某些打包/压缩组合下于 `app.modal.show()` 里丢掉 content 方法, 表现为
//   `TypeError: this.content is not a function` —— modal 整个渲染不出来。
//   ⇒ 依赖方向固定成 LessonReviews → LessonReviewModal → LessonReviewMedia, 无环。
import {  openMediaLightbox } from './LessonReviewMedia';
export {  openMediaLightbox }
import {  uploadMedia } from './LessonReviewMedia';
export {  uploadMedia }
import {  mediaTypeFor  } from './LessonReviewMedia';
export {  mediaTypeFor  };

/**
 * v0.18.0 (辉哥 10/18 拍板) — 「课评」的共享前端工具 (三处调用点共用)
 *
 * ## 三态入口的三个状态是什么
 *   null                → 从未写:  蓝 #2D7BE5 + `fas fa-star`
 *   {status:'draft'}    → 草稿:    橙 #ff9500 + **`fas fa-pen-to-square`**
 *   {status:'published'}→ 已发布:  绿 #34c759 + `fas fa-star` + 行内 ★★★★★ 5.0
 *
 * ## 🔴 WCAG 2.1 SC 1.4.1 —— 三态**只有图标不同**可区分 (辉哥选定的方案 A)
 *   文案三态**全是「课评」**, 背景色全透明, 边框宽度全是 0px。
 *   ⇒ 草稿行与未写行之间唯一的非颜色线索就是**图标的形状** (铅笔 vs 星)。
 *   少了这个图标差异, 转灰度后这两行**完全一样** —— 1.4.1 违规。
 *   `iconClassFor()` 是这条规则的**唯一**实现, 改别处之前先看这里。
 *
 * ## ⛔ 草稿数据**不会**由前端过滤
 *   服务端 ListLessonReviewsController 对学员只返回 published (R2)。
 *   前端拿到 draft 只可能发生在「员工视角」下 —— 那是教练自己的草稿箱, 不是泄露。
 */

/** 状态 → 图标类名。⛔ 三态里**只有** draft 用别的形状, 这是 1.4.1 的落点。 */
export function iconClassFor(status) {
  return status === 'draft' ? 'fas fa-pen-to-square' : 'fas fa-star';
}

/** 状态 → 入口的 CSS 修饰类 (颜色由 less 决定, 这里只管类名) */
export function stateClassFor(status) {
  if (status === 'draft') return 'drafting';
  if (status === 'published') return 'published';
  return 'none';
}

/**
 * 行内星级: **恒 5 颗** —— n 颗实心 + (5−n) 颗空心, 后面跟数字。
 * 已发布态才显示 (规则: 从未写与草稿**都没有**行内星级)。
 *
 * 🔴 v0.18.6 (辉哥 10/18) — 行内星级改用**与课评弹窗同一套 FontAwesome 图标**
 *
 * ## 为什么从「文本字符 ★/☆」改成 FA 图标
 *   改前 `starsFor()` 渲染的是 **U+2605 `★` / U+2606 `☆` 文本字符**,
 *   弹窗 `LessonReviewModal.starsRow()` 渲染的是 **`fa fa-star` / `far fa-star` 图标**。
 *   两边字形完全不同 (系统字体 vs FA 矢量), 同一个「课评 4 分」在列表里和弹窗里长得不一样。
 *   辉哥要求: **行内与弹窗完全一致**。⇒ 这里逐字对齐 `starsRow()` 的类名写法。
 *   ⚠️ 对齐的是**类名写法**, 字号/间距按操作轨预算另算 (弹窗 22px vs 行内 12px)。
 *
 * ## ⛔ 为什么空心必须是 `far fa-star`, 不是 `fa-star-o`
 *   `fa-star-o` 是 FontAwesome **4** 的老类, 本仓是 **FA Free 7.3.1** ——
 *   实测下发 CSS 里独立 `.fa-star-o` 定义数 **0**, 用了**静默不渲染字形** (类名在、像素没有)。
 *   ⇒ 一律 `far fa-star` + `is-empty` 类 (类在 CSS 里实测存在)。
 *   验收也必须**验字形** (`::before` 的 content 非 none 且渲染宽度 > 0), 不能只验类名存在。
 *
 * ## ⛔⛔ 为什么数字从 `<i>` 改成 `<span>`
 *   ① **语义**: 评分要「恰好 5 个 `<i>`」, 数字不是星, 不该占用同一个标签。
 *   ② `<i>` 有 UA 默认 `font-style: italic` (v0.18.1/v0.18.2 连踩两次)。
 *      星星带 `fa`/`far`, FA 基类已压掉斜体; **数字不带**, 改 `<span>` 就不用再靠 LESS 兜底。
 *   ③ ⚠️ 但 `<span>` 有自己的坑: 学员端 `.ZS-StudentLessonsModal .ZS-sl-f span { color: @text }`
 *      是 (0,2,1), 会把数字打成灰白。⇒ 本轮在 LESS 里给 `.ZS-review-inline-score`
 *      补了 (0,4,0) 的显式规则压过它。**改这条规则前先看三条路径的实测颜色**。
 *
 * ## ⛔ 颜色一律**由容器给**, 本函数只管类名
 *   三条调用路径, LESS 里各有一条显式规则 (不靠继承):
 *     1. `LessonsTab.reviewStars()` — 套 `.ZS-review-inline-stars` (桌面 + 移动共用)
 *     2. `LessonsTab.reviewStars()` — 移动端同一条, 另有一条 `.ZS-lesson-mobile-actions` 兜底
 *     3. `StudentLessonsModal.renderReviewEntry()` — 套 `.ZS-sl-review`, **不套**上面那个容器
 *   ⚠️ 上一轮就是靠继承出的事故: 第 3 条不套容器 ⇒ 星级继承成 `.ZS-sl-review` 的绿,
 *     连 `is-empty` 的灰星一起变绿, 空心/实心在学员端**完全无法区分**。
 *
 * ## ⛔⛔ 回调参数绝不能叫 `m`
 *   JSX 编译后的 hyperscript 就是全局 `m()`; 写成 `map((m, i) => …)` 会把它遮蔽成
 *   `TypeError: m is not a function` 且**编译期零告警**。本函数干脆用 `for` 循环, 压根没有回调。
 */
export function starsFor(rating) {
  const n = Math.max(0, Math.min(5, parseInt(rating, 10) || 0));
  // ⛔ 0 分 / 未写 ⇒ 一颗都不渲染。**保持旧行为不变** (辉哥: 别默默改这个)。
  if (n <= 0) return '';
  const out = [];
  for (let i = 1; i <= 5; i++) {
    // 类名写法与 LessonReviewModal.starsRow() **逐字一致** (只差 font-size/间距)。
    out.push(m("i", {
      key: i,
      "aria-hidden": "true",
      className: 'ZS-review-inline-star fa ' + (i <= n ? 'fa-star' : 'far fa-star is-empty')
    }));
  }
  out.push(m("span", {
    key: "ZS-review-inline-score",
    className: "ZS-review-inline-score"
  }, n.toFixed(1)));
  return out;
}

/**
 * 批量查课评三态。
 *
 * ⚠️ 失败**静默降级成「全部没有课评」**: 课评是列表的附加列, 拿不到就不显示入口,
 *    绝不能因为它把整个列表搞成错误态。返回的永远是 object (不会是 undefined),
 *    调用方 `map[id]` 直接安全取值。
 */
export async function fetchReviewMap(ids) {
  const list = (ids || []).map(Number).filter(n => n > 0);
  if (list.length === 0) return {};
  try {
    const res = await Api.get('lesson-reviews', {
      ids: list.join(',')
    });
    return res && res.reviews || {};
  } catch (e) {
    console.warn('[zs] lesson reviews batch load failed', e);
    return {};
  }
}

/**
 * 打开课评 modal。
 *
 * @param {object} opts
 * @param {object} opts.lesson      课次行数据 (LessonSerializer 形状)
 * @param {object|null} opts.descriptor 列表页已拿到的三态 (可为 null = 从未写)
 * @param {boolean} opts.readonly   true = 学员端只读 (规则 20: 无任何可编辑控件)
 * @param {Function} opts.onChange  保存成功后回调 (让列表刷新那一行)
 */
export function openReviewModal(_ref) {
  let {
    lesson,
    descriptor = null,
    readonly = false,
    onChange = null
  } = _ref;
  app.modal.show(LessonReviewModal, {
    lesson,
    descriptor,
    readonly,
    onChange
  });
}

/**
 * 🔴 fancybox 灯箱 —— 图片/视频混装, **视频必须写 `type: 'html5video'`**
 *
 * ## ⚠️ 为什么是 'html5video' 而不是 'video'
 *   fancybox 6.x 内置的 `video` 是 **Vimeo/YouTube 那类第三方**播放器的类型;
 *   HTML5 `<video>` 的类型常量字面量就是 `'html5video'`。写 `'video'` **静默无效**
 *   (不报错, 灯箱打开是空白)。
 *
 * ## ⚠️ 为什么先触发加载再轮询
 *   ziven-core 是**静默异步**加载 fancybox 的, 页面上任何时刻点开都可能 `window.Fancybox`
 *   还不存在。照抄 bjxy 的 `openFancyboxGallery`: 先用 `app.fancyboxOpen(urls[0])` 触发加载,
 *   再轮询等全局出现。课评媒体又是**异步渲染**的, 首点尤其容易落空。
 *
 * ## ⚠️ z-index: 局部处理, 不动全局变量
 *   `--fancybox-zIndex` 默认 1050, 本扩展 `.zs-rsv-*` 是 99997/99998/99999。
 *   **绝不**全局改那个变量 (会盖不过它们, 还会污染 bjxy 的评价图库) ——
 *   处理方式见 LessonReviewModal 的 bumpModalLayer()。
 */
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/LessonReviews', { openMediaLightbox: openMediaLightbox,uploadMedia: uploadMedia,mediaTypeFor: mediaTypeFor,iconClassFor: iconClassFor,stateClassFor: stateClassFor,starsFor: starsFor,openReviewModal: openReviewModal, });